import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { getUserGroups } from '@/modules/common/api/user-groups.api';
import { SingleSelectOption } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { UserGroup } from '@/modules/common/types/user-group';

export const USER_GROUPS_QUERY_KEY = ['user-groups'];

const USER_GROUPS_STALE_TIME = 1000 * 60 * 10; // 10 minutes — groups rarely change

/**
 * Fetches user groups from the API and transforms them for use in filters and selects
 * Returns user groups as select options with id and name
 */
export const useUserGroups = () => {
  const query = useQuery({
    queryKey: USER_GROUPS_QUERY_KEY,
    queryFn: getUserGroups,
    staleTime: USER_GROUPS_STALE_TIME,
    select: (data): SingleSelectOption[] => {
      return data.map(group => ({
        value: group.id.toString(),
        label: group.name,
      }));
    },
  });

  return {
    ...query,
    groupOptions: query.data ?? [],
  };
};

/**
 * Raw user groups, including `isDefault`.
 *
 * Shares `USER_GROUPS_QUERY_KEY` with {@link useUserGroups}, so both hooks are backed by a single
 * fetch. Use `useUserGroups` when you only need `{ value, label }` options — its `select` collapses
 * groups into `SingleSelectOption[]` and drops every other field.
 */
export const useUserGroupsList = ({ enabled = true }: { enabled?: boolean } = {}): UseQueryResult<
  UserGroup[]
> => {
  return useQuery({
    queryKey: USER_GROUPS_QUERY_KEY,
    queryFn: getUserGroups,
    staleTime: USER_GROUPS_STALE_TIME,
    enabled,
  });
};
